import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../services/api";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

const API = {
  REPORTS: `${API_BASE_URL}/api/orders/order-reports`,
  EXPORT: `${API_BASE_URL}/api/orders/order-reports/export`,
  REF_CENTERS: `${API_BASE_URL}/api/reference-centers`,
  DOCTORS: `${API_BASE_URL}/api/doctors`,
  DIAG_CENTERS: `${API_BASE_URL}/api/diagnostic-centers`,
  CENTERS: `${API_BASE_URL}/api/centers`,
  SOURCE: `${API_BASE_URL}/api/source`,
};

const toISODate = (d) => {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch (e) {
    return "";
  }
};

export default function OrderReportFilters() {
  const [refCenters, setRefCenters] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [diagnosticCenters, setDiagnosticCenters] = useState([]);
  const [centers, setCenters] = useState([]);
  const [sources, setSources] = useState([]);

  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [exporting, setExporting] = useState(false);

  // ✅ Tabs
  const TABS = {
    TODAY: "today",
    SPECIFIC_DAY: "specific_day",
    BY_MONTH: "by_month",
    BY_RANGE: "by_range",
  };
  const [activeTab, setActiveTab] = useState(TABS.TODAY);

  // Pagination state from backend
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [stats, setStats] = useState({
    total: 0,
    amount: 0,
    completed: 0,
    pending: 0,
  });

  const [filters, setFilters] = useState({
    orderId: "",
    // Dates
    date: "",
    fromDate: "",
    toDate: "",
    month: "", // ✅ NEW for by-month tab (YYYY-MM)

    // Dropdowns
    centerId: "",
    refCenterId: "",
    doctorId: "", // (Referred by)
    diagnosticCenterId: "",
    status: "",
    source: "",
    paymentStatus: "",

    // Optional
    category: "", // ✅ NEW (like screenshot)
    city: "",
    pincode: "",
  });

  const navigate = useNavigate();
  const handleViewDetails = (orderId) => navigate(`/orders/${orderId}`);

  // Load dropdowns on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [refRes, docRes, diagRes, centerRes, sourceRes] =
          await Promise.all([
            axios.get(API.REF_CENTERS),
            axios.get(API.DOCTORS),
            axios.get(API.DIAG_CENTERS),
            axios.get(API.CENTERS),
            axios.get(API.SOURCE),
          ]);

        setRefCenters(refRes.data.data || []);
        setDoctors(docRes.data.doctors || []);
        setDiagnosticCenters(diagRes.data.data || []);
        setCenters(centerRes.data.data || []);
        setSources(sourceRes.data.data || []);
      } catch (err) {
        console.error("Error loading dropdowns:", err);
      }
    };

    fetchDropdowns();
  }, []);

  const handleChange = (e) => {
    try {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    } catch (err) {
      console.error("handleChange error:", err);
    }
  };

  // ✅ When tab changes, reset only date-related fields (keep dropdown selections)
  const onTabChange = (tabKey) => {
    try {
      setActiveTab(tabKey);
      setCurrentPage(1);

      setFilters((prev) => ({
        ...prev,
        date: "",
        fromDate: "",
        toDate: "",
        month: "",
      }));
    } catch (err) {
      console.error("onTabChange error:", err);
    }
  };

  const clearFilters = () => {
    try {
      setFilters({
        orderId: "",
        date: "",
        fromDate: "",
        toDate: "",
        month: "",

        centerId: "",
        refCenterId: "",
        doctorId: "",
        diagnosticCenterId: "",
        status: "",
        source: "",
        paymentStatus: "",

        category: "",
        city: "",
        pincode: "",
      });
      setReports([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setStats({ total: 0, amount: 0, completed: 0, pending: 0 });
      setActiveTab(TABS.TODAY);
    } catch (err) {
      console.error("clearFilters error:", err);
    }
  };

  // ✅ Build params depending on active tab
  const buildParams = (page, limit) => {
    try {
      const base = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      );

      // remove date fields first, then apply per-tab
      delete base.date;
      delete base.fromDate;
      delete base.toDate;
      delete base.month;

      const today = toISODate(new Date());

      if (activeTab === TABS.TODAY) {
        // choose one approach:
        // A) if backend supports date exact filter:
        base.date = today;
        // B) if backend prefers range, use:
        // base.fromDate = today; base.toDate = today;
      } else if (activeTab === TABS.SPECIFIC_DAY) {
        if (filters.date) base.date = filters.date;
      } else if (activeTab === TABS.BY_MONTH) {
        // send month as YYYY-MM (backend can parse)
        if (filters.month) base.month = filters.month;
      } else if (activeTab === TABS.BY_RANGE) {
        if (filters.fromDate) base.fromDate = filters.fromDate;
        if (filters.toDate) base.toDate = filters.toDate;
      }

      return { ...base, page, limit };
    } catch (err) {
      console.error("buildParams error:", err);
      return { page, limit };
    }
  };

  const fetchReports = async (page = 1, limit = pageSize) => {
    setLoading(true);
    try {
      const params = buildParams(page, limit);

      const res = await axiosInstance.get(API.REPORTS, { params });
      const responseData = res.data;
      const data = responseData.data || [];

      setReports(data);
      setCurrentPage(responseData.page || page);
      setTotalPages(responseData.totalPages || 1);
      setTotalRecords(responseData.total || 0);

      const totalAmount = data.reduce(
        (sum, order) => sum + (order.finalAmount || 0),
        0
      );
      const completedCount = data.filter(
        (order) => order.status === "completed"
      ).length;
      const pendingCount = data.filter(
        (order) => order.status === "pending"
      ).length;

      setStats({
        total: data.length,
        amount: totalAmount,
        completed: completedCount,
        pending: pendingCount,
      });
    } catch (err) {
      console.error("Error fetching reports:", err);
      alert("Error fetching reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    try {
      setCurrentPage(page);
      fetchReports(page, pageSize);
    } catch (err) {
      console.error("handlePageChange error:", err);
    }
  };

  const handlePageSizeChange = (size) => {
    try {
      setPageSize(size);
      setCurrentPage(1);
      fetchReports(1, size);
    } catch (err) {
      console.error("handlePageSizeChange error:", err);
    }
  };

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const params = buildParams(1, 999999); // export all for current filter set

      const res = await axiosInstance.get(API.EXPORT, {
        params,
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Order-Report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Excel download failed");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN");
    } catch (e) {
      return "-";
    }
  };

  const calculateSlNo = (index) => (currentPage - 1) * pageSize + index + 1;

  // Pagination buttons (same as yours)
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    if (currentPage > 1) {
      buttons.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-l hover:bg-gray-50"
        >
          &laquo; Prev
        </button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-xs font-medium border ${
            i === currentPage
              ? "z-10 bg-blue-600 text-white border-blue-600"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages) {
      buttons.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-r hover:bg-gray-50"
        >
          Next &raquo;
        </button>
      );
    }

    return buttons;
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        isMenuOpen={sidebarOpen}
      />
 <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-14 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="min-h-screen bg-gray-50 p-3 md:p-4">
          {/* Top title */}
          <div className="mb-3">
            <div className="flex items-center gap-2 text-gray-800">
              <span className="text-sm font-semibold">LAB ORDER STATISTICS</span>
            </div>
          </div>

          {/* ✅ FILTER STRIP LIKE YOUR SCREENSHOT */}
          <div className="bg-white border border-gray-200 rounded shadow-sm overflow-hidden">
            {/* Tabs */}
            <div className="flex items-center gap-6 px-3 py-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => onTabChange(TABS.TODAY)}
                className={`text-xs ${
                  activeTab === TABS.TODAY
                    ? "text-gray-900 font-semibold"
                    : "text-blue-600 hover:underline"
                }`}
              >
                Today
              </button>
              <button
                onClick={() => onTabChange(TABS.SPECIFIC_DAY)}
                className={`text-xs ${
                  activeTab === TABS.SPECIFIC_DAY
                    ? "text-gray-900 font-semibold"
                    : "text-blue-600 hover:underline"
                }`}
              >
                Specific Day
              </button>
              <button
                onClick={() => onTabChange(TABS.BY_MONTH)}
                className={`text-xs ${
                  activeTab === TABS.BY_MONTH
                    ? "text-gray-900 font-semibold"
                    : "text-blue-600 hover:underline"
                }`}
              >
                By Month
              </button>
              <button
                onClick={() => onTabChange(TABS.BY_RANGE)}
                className={`text-xs ${
                  activeTab === TABS.BY_RANGE
                    ? "text-gray-900 font-semibold"
                    : "text-blue-600 hover:underline"
                }`}
              >
                By Date Range
              </button>

              <div className="ml-auto">
                <button
                  onClick={clearFilters}
                  className="text-[11px] text-gray-500 hover:text-gray-900"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Row controls */}
            <div className="p-3">
              <div className="flex flex-wrap items-center gap-2">
                {/* Referred By (Doctor) */}
                <select
                  name="doctorId"
                  value={filters.doctorId}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">Select Referred by</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>

                {/* Ref Center */}
                <select
                  name="refCenterId"
                  value={filters.refCenterId}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">Select Ref. Center</option>
                  {refCenters.map((rc) => (
                    <option key={rc.id} value={rc.id}>
                      {rc.name}
                    </option>
                  ))}
                </select>

                {/* Source */}
                <select
                  name="source"
                  value={filters.source}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">Select Source</option>
                  {(sources || []).map((s, idx) => (
                    <option key={idx} value={s?.name || ""}>
                      {s?.name || ""}
                    </option>
                  ))}
                </select>

                {/* Category */}
                <input
                  name="category"
                  value={filters.category}
                  onChange={handleChange}
                  placeholder="Category"
                  className="h-8 text-xs border border-gray-300 rounded px-2 w-[160px]"
                />

                {/* ✅ Tab-wise date inputs */}
                {activeTab === TABS.SPECIFIC_DAY && (
                  <input
                    type="date"
                    name="date"
                    value={filters.date}
                    onChange={handleChange}
                    className="h-8 text-xs border border-gray-300 rounded px-2"
                  />
                )}

                {activeTab === TABS.BY_MONTH && (
                  <input
                    type="month"
                    name="month"
                    value={filters.month}
                    onChange={handleChange}
                    className="h-8 text-xs border border-gray-300 rounded px-2"
                  />
                )}

                {activeTab === TABS.BY_RANGE && (
                  <>
                    <input
                      type="date"
                      name="fromDate"
                      value={filters.fromDate}
                      onChange={handleChange}
                      className="h-8 text-xs border border-gray-300 rounded px-2"
                    />
                    <input
                      type="date"
                      name="toDate"
                      value={filters.toDate}
                      onChange={handleChange}
                      className="h-8 text-xs border border-gray-300 rounded px-2"
                    />
                  </>
                )}

                {/* Search */}
                <button
                  onClick={() => fetchReports(1, pageSize)}
                  disabled={loading}
                  className="h-8 px-4 text-xs font-semibold rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Searching..." : "Search"}
                </button>

                {/* Excel */}
                <button
                  onClick={downloadExcel}
                  disabled={exporting}
                  className="h-8 px-3 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {exporting ? "Downloading..." : "Export"}
                </button>
              </div>

              {/* (Optional) extra filters row - keep if you want */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                <select
                  name="centerId"
                  value={filters.centerId}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">All Centers</option>
                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <select
                  name="diagnosticCenterId"
                  value={filters.diagnosticCenterId}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">All Venue Centers</option>
                  {diagnosticCenters.map((dc) => (
                    <option key={dc.id} value={dc.id}>
                      {dc.name}
                    </option>
                  ))}
                </select>

                <select
                  name="status"
                  value={filters.status}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                <select
                  name="paymentStatus"
                  value={filters.paymentStatus}
                  onChange={handleChange}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                >
                  <option value="">All Payments</option>
                  <option value="pending">Due</option>
                  <option value="paid">Paid</option>
                </select>

                <input
                  type="text"
                  name="orderId"
                  value={filters.orderId}
                  onChange={handleChange}
                  placeholder="Order ID"
                  className="h-8 text-xs border border-gray-300 rounded px-2"
                />
              </div>
            </div>
          </div>

          {/* ✅ your table section can stay same (I kept it minimal here) */}
          {reports.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-4">
              <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <h3 className="text-base font-semibold text-gray-900">
                  Results ({totalRecords.toLocaleString()} orders)
                </h3>
                <div className="text-xs text-gray-500">
                  Showing{" "}
                  {((currentPage - 1) * pageSize + 1).toLocaleString()} to{" "}
                  {Math.min(currentPage * pageSize, totalRecords).toLocaleString()}{" "}
                  of {totalRecords.toLocaleString()} orders
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-3 py-2 text-left">SL No</th>
                      <th className="px-3 py-2 text-left">Order Details</th>
                      <th className="px-3 py-2 text-left">Patient</th>
                      <th className="px-3 py-2 text-left">Venue</th>
                      <th className="px-3 py-2 text-left">B2B Info</th>
                      <th className="px-3 py-2 text-left">Address</th>
                      <th className="px-3 py-2 text-left">Financial</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {reports.map((order, index) => (
                      <tr
                        key={order.id}
                        onClick={() => handleViewDetails(order.id)}
                        className="cursor-pointer hover:bg-gray-50/80 transition-colors duration-150"
                      >
                        <td className="px-3 py-2 text-gray-500 font-medium">
                          {calculateSlNo(index)}
                        </td>

                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">
                            ORD -{order.id}
                          </div>
                          <div className="flex items-center mt-1.5 gap-1">
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800">
                              {order.source || "Unknown"}
                            </span>
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800">
                              {order.isHomeSample ? "Home" : "Center"}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">
                            {order.patient?.fullName || "-"}
                          </div>
                          <div className="text-gray-500 mt-0.5">
                            {order.patient?.contactNo || "-"}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          {order.diagnosticCenter?.name || "-"}
                        </td>

                        <td className="px-3 py-2">
                          {order.center ? (
                            <div className="space-y-1">
                              <div className="truncate max-w-[170px]">
                                <span className="text-gray-500">Center:</span>{" "}
                                {order.center?.name || "-"}
                              </div>
                              <div className="truncate max-w-[170px]">
                                <span className="text-gray-500">mobile:</span>{" "}
                                {order.center?.mobile || "-"}
                              </div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td className="px-3 py-2">
                          <div className="space-y-0.5 max-w-[260px]">
                            <div className="text-gray-900 truncate">
                              {order.address?.address || "-"}
                            </div>
                            <div className="text-gray-500">
                              {order.address?.city ? `${order.address.city}` : ""}
                              {order.address?.pincode
                                ? ` • ${order.address.pincode}`
                                : ""}
                            </div>
                            <div className="text-gray-400">
                              {order.address?.state || ""}
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">
                            ₹{order.finalAmount || 0}
                          </div>
                          <div className="text-gray-500">
                            Total: ₹{order.totalAmount || 0}
                          </div>
                          <div className="text-green-600">
                            Disc: ₹{order.discount || 0}
                          </div>
                          <div className="mt-1">
                            <span
                              className={`px-2 py-0.5 font-medium rounded-full ${
                                String(order.paymentStatus).toLowerCase() === "paid"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {String(order.paymentStatus).toLowerCase() === "paid" ? "Paid" : "Due"}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            <span
                              className={`px-2 py-0.5 font-medium rounded-full ${
                                order.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : order.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : order.status === "cancelled"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {order.status || "Unknown"}
                            </span>
                            <div className="text-gray-500">
                              {formatDate(order.date)}{" "}
                              {order.slot ? `• ${order.slot}` : ""}
                            </div>
                            <div className="text-gray-500">
                              Created: {formatDate(order.createdAt)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-700">Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center">
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                      {renderPaginationButtons()}
                    </nav>
                  </div>

                  <div className="text-xs text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && reports.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center mt-4">
              <h3 className="text-base font-medium text-gray-900 mb-1.5">
                No reports found
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Apply filters and search to view order reports
              </p>
              <button
                onClick={() => fetchReports(1, pageSize)}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
              >
                Try Search Anyway
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}